import {
  ActionField,
  ActionFieldChildProps,
  useFormContext,
  withForm,
} from '@oscar/form';
import { Col, Row } from '@oscar/theme';
import { useDeepCompareEffect } from '@oscar/utils';
import {
  MessageSuccess,
  PageHeader,
  StatusTagColumnTable,
} from 'common/components';
import { FORMAT_DATE, MESSAGE_NOTICE, REQUEST_STATUS } from 'common/constants';
import { APIS } from 'common/constants/endpoints';
import {
  INSULATION_HEADERS,
  LIST_KEY_CREATE_REQUEST,
} from 'common/constants/insulation';
import { useAuth } from 'features/auth';
import {
  ActivityLog,
  InsulationDetail,
  MAP_REQUEST_NAME,
  ProposalDetail,
  QaqcDetail,
  RequestDataType,
  RequestorDetail,
  ScaffoldDetail,
  WorkDetail,
  WorkFlow,
  PTW,
} from 'features/insulation';
import { ReinstatementDetail } from 'features/insulation/components/ReinstatementDetail';
import { RemovalDetail } from 'features/insulation/components/RemovalDetail';
import SubmitComp from 'features/insulation/components/SubmitComp';
import { get } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import VisibleView from './components/VisibleView';
import useStatus from './hooks/useStatus';
import './style.scss';
import { getFieldsValidateReinstatements, validateData } from './utils';

const INSTATEMENT_PANELS: RequestDataType = {
  DRAFT: [
    {
      key: LIST_KEY_CREATE_REQUEST.REQUESTOR_DETAIL,
      header: INSULATION_HEADERS.REQUESTOR_DETAIL,
      component: RequestorDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: ['section', 'discipline'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.WORK_DETAIL,
      header: INSULATION_HEADERS.WORK_DETAIL,
      component: WorkDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: [(data: any) => data?.isWorkDetailRequire && 'listWorkDetail'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SCAFFOLD_DETAIL,
      header: INSULATION_HEADERS.SCAFFOLD_DETAIL,
      component: ScaffoldDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: [
        (data: any) => data?.isScaffoldingRequire && 'listScaffoldDetail',
      ],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.INSULATION_DETAIL,
      header: INSULATION_HEADERS.INSULATION_DETAIL,
      component: InsulationDetail,
      roles: [['DRAFT', 'SUBMITTED']],
      validate: [
        'siteVisitDate',
        'area',
        'endDate',
        'workDescription',
        'startDate',
      ],
    },
  ],
  PENDING_VALIDTION: [
    {
      key: LIST_KEY_CREATE_REQUEST.REQUESTOR_DETAIL,
      header: INSULATION_HEADERS.REQUESTOR_DETAIL,
      component: RequestorDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'INSULATION'],
      ],
      validate: ['section', 'discipline'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.WORK_DETAIL,
      header: INSULATION_HEADERS.WORK_DETAIL,
      component: WorkDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'INSULATION'],
      ],
      validate: [(data: any) => data?.isWorkDetailRequire && 'listWorkDetail'],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SCAFFOLD_DETAIL,
      header: INSULATION_HEADERS.SCAFFOLD_DETAIL,
      component: ScaffoldDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'INSULATION'],
      ],
      validate: [
        (data: any) => data?.isScaffoldingRequire && 'listScaffoldDetail',
      ],
    },
    {
      key: LIST_KEY_CREATE_REQUEST.INSULATION_DETAIL,
      header: INSULATION_HEADERS.INSULATION_DETAIL,
      component: InsulationDetail,
      roles: [
        ['PENDING_VALIDTION', 'EDITED'],
        ['PENDING_VALIDTION', 'SUBMITTED', 'INSULATION'],
      ],
      validate: [
        'siteVisitDate',
        'area',
        'endDate',
        'workDescription',
        'startDate',
        (_data, helpers) => {
          if (
            helpers?.checkRole?.('PENDING_VALIDTION', 'SUBMITTED', 'INSULATION')
          ) {
            return 'serviceProviderId';
          }
        },
        (_data, helpers) => {
          if (
            helpers?.checkRole?.('PENDING_VALIDTION', 'SUBMITTED', 'INSULATION')
          ) {
            return 'qaqcSupervisorName';
          }
        },
      ],
    },
  ],
  PENDING_PROPOSAL: [
    {
      key: LIST_KEY_CREATE_REQUEST.REQUESTOR_DETAIL,
      header: INSULATION_HEADERS.REQUESTOR_DETAIL,
      component: RequestorDetail,
      roles: [['PENDING_PROPOSAL', 'EDITED']],
      validate: ['section', 'discipline'],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.WORK_DETAIL,
      header: INSULATION_HEADERS.WORK_DETAIL,
      component: WorkDetail,
      roles: [['PENDING_PROPOSAL', 'EDITED']],
      validate: [(data: any) => data?.isWorkDetailRequire && 'listWorkDetail'],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SCAFFOLD_DETAIL,
      header: INSULATION_HEADERS.SCAFFOLD_DETAIL,
      component: ScaffoldDetail,
      roles: [['PENDING_PROPOSAL', 'EDITED']],
      validate: [
        (data: any) => data?.isScaffoldingRequire && 'listScaffoldDetail',
      ],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.INSULATION_DETAIL,
      header: INSULATION_HEADERS.INSULATION_DETAIL,
      component: InsulationDetail,
      roles: [['PENDING_PROPOSAL', 'EDITED']],
      validate: [
        'siteVisitDate',
        'area',
        'endDate',
        'workDescription',
        'startDate',
      ],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [['PENDING_PROPOSAL', 'SUBMITTED']],
      validate: [
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_PROPOSAL', 'SUBMITTED')) {
            return 'proposalBillingDetail.data';
          }
        },
      ],
      isVisibleByRole: true,
    },
  ],
  PENDING_REMOVAL_PTW: [
    {
      key: LIST_KEY_CREATE_REQUEST.PTW,
      header: INSULATION_HEADERS.PTW,
      component: PTW,
      roles: [['PENDING_REMOVAL', 'EDITED', 'INSULATION']],
      validate: [
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REMOVAL', 'EDITED', 'INSULATION')) {
            return 'listPTWDetail';
          }
        },
      ],
      isVisibleByRole: true,
    },
  ],
  PENDING_REMOVAL_COMPLETION: [
    {
      key: LIST_KEY_CREATE_REQUEST.PTW,
      header: INSULATION_HEADERS.PTW,
      component: PTW,
      roles: [['PENDING_REMOVAL', 'EDITED', 'INSULATION']],
      validate: [
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REMOVAL', 'EDITED', 'INSULATION')) {
            return 'listPTWDetail';
          }
        },
      ],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.REMOVAL_DETAIL,
      header: INSULATION_HEADERS.REMOVAL_DETAIL,
      component: RemovalDetail,
      roles: [['PENDING_REMOVAL', 'SUBMITTED']],
      validate: [
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REMOVAL', 'SUBMITTED')) {
            return 'insulationRemovalDetail.completationDate';
          }
        },
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REMOVAL', 'SUBMITTED')) {
            return _data?.insulationRemovalDetail?.workLeaderName
              ? 'insulationRemovalDetail.workLeaderName'
              : 'insulationRemovalDetail.workLeaderId';
          }
        },
      ],
      isVisibleByRole: true,
    },
  ],
  IN_SERVICE: [
    {
      key: LIST_KEY_CREATE_REQUEST.WORK_DETAIL,
      header: INSULATION_HEADERS.WORK_DETAIL,
      component: WorkDetail,
      roles: [['IN_SERVICE', 'EDITED']],
      validate: [(data: any) => data?.isWorkDetailRequire && 'listWorkDetail'],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.SCAFFOLD_DETAIL,
      header: INSULATION_HEADERS.SCAFFOLD_DETAIL,
      component: ScaffoldDetail,
      roles: [['IN_SERVICE', 'EDITED']],
      validate: [
        (data: any) => data?.isScaffoldingRequire && 'listScaffoldDetail',
      ],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.INSULATION_DETAIL,
      header: INSULATION_HEADERS.INSULATION_DETAIL,
      component: InsulationDetail,
      roles: [['IN_SERVICE', 'EDITED']],
      validate: [
        'siteVisitDate',
        'area',
        'endDate',
        'workDescription',
        'startDate',
      ],
      isVisibleByRole: true,
    },
  ],
  PENDING_REINSTATEMENT_PTW: [
    {
      key: LIST_KEY_CREATE_REQUEST.PTW,
      header: INSULATION_HEADERS.PTW,
      component: PTW,
      roles: [['PENDING_REINSTATEMENT', 'EDITED', 'INSULATION']],
      validate: [
        (_data, helpers) => {
          if (
            helpers?.checkRole?.(
              'PENDING_REINSTATEMENT',
              'EDITED',
              'INSULATION',
            )
          ) {
            const length = _data?.ePTWNo?.ePTWNoRemoval?.length;
            return `listPTWDetail[${length}]`;
          }
        },
      ],
      isVisibleByRole: true,
    },
  ],
  PENDING_REINSTATEMENT_COMPLETION: [
    {
      key: LIST_KEY_CREATE_REQUEST.PTW,
      header: INSULATION_HEADERS.PTW,
      component: PTW,
      roles: [['PENDING_REINSTATEMENT', 'EDITED', 'INSULATION']],
      validate: [
        (_data, helpers) => {
          if (
            helpers?.checkRole?.(
              'PENDING_REINSTATEMENT',
              'EDITED',
              'INSULATION',
            )
          ) {
            return 'listPTWDetail';
          }
        },
      ],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [['PENDING_REINSTATEMENT', 'SUBMITTED']],
      validate: [
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REINSTATEMENT', 'SUBMITTED')) {
            return 'proposalBillingDetail.data';
          }
        },
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REINSTATEMENT', 'SUBMITTED')) {
            return 'proposalBillingDetail.poNumber';
          }
        },
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REINSTATEMENT', 'SUBMITTED')) {
            return 'proposalBillingDetail.totalFinalCost';
          }
        },
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REINSTATEMENT', 'SUBMITTED')) {
            return getFieldsValidateReinstatements(_data);
          }
        },
      ],
      isVisibleByRole: true,
    },
    {
      key: LIST_KEY_CREATE_REQUEST.REINSTATEMENT_DETAIL,
      header: INSULATION_HEADERS.REINSTATEMENT_DETAIL,
      component: ReinstatementDetail,
      roles: [['PENDING_REINSTATEMENT', 'SUBMITTED']],
      validate: [
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REINSTATEMENT', 'SUBMITTED')) {
            return 'insulationReinstatementDetail.completionDate';
          }
        },
        (_data, helpers) => {
          if (helpers?.checkRole?.('PENDING_REINSTATEMENT', 'SUBMITTED')) {
            return 'insulationReinstatementDetail.workLeaderName';
          }
        },
      ],
      isVisibleByRole: true,
    },
  ],

  PENDING_QAQC: [
    {
      key: LIST_KEY_CREATE_REQUEST.QAQC_DETAIL,
      header: INSULATION_HEADERS.QAQC_DETAIL,
      component: QaqcDetail,
      roles: [['PENDING_QAQC', 'SUBMITTED', 'INSULATION']],
      validate: [
        'reinstatementChecklist.overallAcceptance',
        'reinstatementChecklist.inspectionDate',
      ],
    },
  ],
  BILLING: [
    {
      key: LIST_KEY_CREATE_REQUEST.PROPOSAL_DETAIL,
      header: INSULATION_HEADERS.PROPOSAL_DETAIL,
      component: ProposalDetail,
      roles: [['BILLING', 'SUBMITTED', 'INSULATION']],
      validate: [
        'proposalBillingDetail.poNumber',
        'proposalBillingDetail.verifiedFinalCost',
      ],
    },
  ],
  CLOSED: [],
};

export const Removal = withForm({
  name: 'removal',
  preload: [
    {
      name: 'metaDataRequestorDetail',
      url: APIS.META_DATA_REQUESTOR_DETAIL,
      method: 'GET',
    },
    {
      name: 'priorities',
      url: APIS.META_DATA_PRIORITY,
      method: 'GET',
    },
    {
      name: 'metaDataInsulationDetail',
      url: APIS.META_DATA_INSULATION_DETAIL,
      method: 'GET',
    },
    {
      name: 'metaDataProposalDetail',
      url: APIS.META_DATA_PROPOSAL_DETAIL,
      method: 'GET',
    },
    {
      name: 'getTokentPTW',
      url: APIS.GET_TOKEN_PTW,
      method: 'GET',
    },
  ],
  initialValues: {
    isScaffoldingRequire: false,
    isWorkDetailRequire: true,
    removalDetail: {
      completionDate: moment(),
    },
    siteVisitDate: moment(
      `${moment().format(FORMAT_DATE.SHORT_MONTH_DATE)} 08:00 AM`,
    ),
  },
  request: {
    name: 'fetchRequestByID',
    method: 'GET',
    nextUrl: (props: any) => {
      return `${APIS.FETCH_REQUEST_BY_CUSTOM_ID}?requestId=${props?.params?.id}`;
    },
    shouldRequest: (props: any) => {
      return !!props?.params?.id;
    },
  },
  size: 'large',
})((props: any) => {
  const [formContext] = useFormContext();
  const [statusRequest] = useStatus();
  const [auth] = useAuth();

  const panelKeys = get(
    INSTATEMENT_PANELS,
    statusRequest?.status || 'DRAFT',
    [],
  )?.map((i: any) => i.key);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState({
    code: 0,
    message: '',
    data: 0,
  });
  const [formStatus, setFormStatus] = useState({});
  const { ePTWNo, lastSubmittedStatus } =
    formContext.selectors?.getInitalValue() || {};

  const removalConfiguration = () => {
    const result: RequestDataType = {};

    for (const key in INSTATEMENT_PANELS) {
      if (key.includes('PENDING_REMOVAL_')) {
        if (
          ePTWNo?.ePTWNoRemoval?.length > 0 ||
          statusRequest?.status !== 'PENDING_REMOVAL'
        ) {
          result['PENDING_REMOVAL'] =
            INSTATEMENT_PANELS['PENDING_REMOVAL_COMPLETION'];
        } else {
          result['PENDING_REMOVAL'] = INSTATEMENT_PANELS['PENDING_REMOVAL_PTW'];
        }
      } else if (key.includes('PENDING_REINSTATEMENT_')) {
        if (
          ePTWNo?.ePTWNoReinstatement?.length > 0 ||
          statusRequest?.status !== 'PENDING_REINSTATEMENT'
        ) {
          result['PENDING_REINSTATEMENT'] =
            INSTATEMENT_PANELS['PENDING_REINSTATEMENT_COMPLETION'];
        } else {
          result['PENDING_REINSTATEMENT'] =
            INSTATEMENT_PANELS['PENDING_REINSTATEMENT_PTW'];
        }
      } else {
        result[key] = INSTATEMENT_PANELS[key];
      }
    }
    return result;
  };

  useDeepCompareEffect(() => {
    formContext.form?.reset?.();
  }, [statusRequest?.state, statusRequest?.id]);

  return (
    <Row wrap={false} gutter={80}>
      <Col flex="310px" className="process-flow">
        <div className="process-flow-ttl">
          <span>Process Flow</span>
        </div>
        <ActionField name="validateData" request={{ name: 'validate' }}>
          {(actionProps: ActionFieldChildProps) => (
            <WorkFlow
              active={statusRequest?.status}
              data={removalConfiguration()}
              status={validateData(
                actionProps,
                removalConfiguration(),
                statusRequest?.status,
                { checkRole: auth.checkRole },
              )}
              getStatusName={status => MAP_REQUEST_NAME[status]}
              statusRequest={statusRequest}
              auth={auth}
              lastSubmitted={lastSubmittedStatus}
            />
          )}
        </ActionField>
      </Col>
      <Col flex="auto">
        <div className="create-insulation">
          <Row>
            <PageHeader
              left={
                <StatusTagColumnTable
                  value={
                    get(REQUEST_STATUS, statusRequest?.status || 'DRAFT')?.VALUE
                  }
                />
              }
              title={
                statusRequest?.id
                  ? statusRequest?.id
                  : 'Create Insulation Request'
              }
              right={statusRequest?.id ? <ActivityLog /> : undefined}
            />
          </Row>
          {isSubmitSuccess.code === 1 && (
            <Row>
              <MessageSuccess
                message={isSubmitSuccess.message}
                type={MESSAGE_NOTICE.SUCCESS}
              />
            </Row>
          )}
          {isSubmitSuccess.code === 2 && (
            <Row>
              <MessageSuccess
                message={isSubmitSuccess.message}
                type={MESSAGE_NOTICE.ERROR}
              />
            </Row>
          )}
          <VisibleView config={removalConfiguration()} />
          <SubmitComp
            statusRequest={statusRequest}
            onSetFormStatus={setFormStatus}
            formStatus={formStatus}
            onResetMessageNotice={setIsSubmitSuccess}
            listPanels={removalConfiguration()}
          />
        </div>
      </Col>
    </Row>
  );
});
export default Removal;
